import { useEffect, useMemo, useRef, useState } from "react";
import { CALL_TYPES, buttonBase, chipButtonBase, panelStyle } from "./dispatchConstants";
import { SearchableDropdown } from "./SearchableDropdown";
import {
  byPreferredUnitOrder,
  getUnitChipStyle,
  isCourtesyDispatchEligible,
  isCourtesyRecommendedUnit,
  isResponseUnit,
  isSupervisor,
  isSupport,
} from "./dispatchUnitUtils";

function unitText(unit) {
  return `${unit?.name || ""} ${unit?.type || ""} ${unit?.category || ""}`.toLowerCase();
}

function isBlockedCourtesyAddUnit(unit) {
  const text = unitText(unit);
  return text.includes("bike") || text.includes("stretcher");
}

function isCourtesyAddableUnit(unit) {
  if (isBlockedCourtesyAddUnit(unit)) return false;
  return (
    isCourtesyDispatchEligible(unit) ||
    isResponseUnit(unit) ||
    isSupervisor(unit) ||
    isSupport(unit)
  );
}

export function DispatchConsolePanel({
  mode = "entry",
  config,
  units,
  debug,
  busy,
  unitSummary,
  callType,
  setCallType,
  location,
  setLocation,
  nature,
  setNature,
  notes,
  setNotes,
  pickupLocation,
  setPickupLocation,
  dropoffLocation,
  setDropoffLocation,
  passengerCount,
  setPassengerCount,
  selectedUnitIds,
  setSelectedUnitIds,
  editingQueuedCallId,
  availableUnits,
  suggested,
  onCreateOrUpdateCall,
  onToggleUnitService,
  onReset,
}) {
  const notesRef = useRef(null);

  const isCourtesy = callType === "courtesyTransport";

  const courtesyRecommendedIds = useMemo(() => {
    return availableUnits
      .filter(isCourtesyRecommendedUnit)
      .sort(byPreferredUnitOrder)
      .map((unit) => unit.id);
  }, [availableUnits]);

  const recommendedAutoIds = isCourtesy
    ? courtesyRecommendedIds.slice(0, 1)
    : suggested?.autoSelectedIds || [];

  const recommendedSuggestedOnlyIds = isCourtesy
    ? []
    : suggested?.suggestedOnlyIds || [];

  const courtesyHasRecommendedUnit = recommendedAutoIds.length > 0;
  const primaryActionIsQueue = isCourtesy && !courtesyHasRecommendedUnit;

  const recommendedAutoKey = recommendedAutoIds.join("|");

  const lastAutoKeyRef = useRef("");

 useEffect(() => {
  if (callType === "standby") {
    setNature("Standby");
  }
}, [callType, setNature]); 

useEffect(() => {
  if (editingQueuedCallId) return;

  const autoKey =
    `${callType}|${location}|${nature}|${recommendedAutoIds.join("|")}`;

  if (lastAutoKeyRef.current === autoKey) {
    return;
  }

  lastAutoKeyRef.current = autoKey;

  if (isCourtesy) {
    const courtesyPrimaryId = recommendedAutoIds[0] || null;
    if (!courtesyPrimaryId) return;

    setSelectedUnitIds((prev) => {
      if (prev.includes(courtesyPrimaryId)) return prev;
      return [courtesyPrimaryId, ...prev];
    });

    return;
  }

  if (recommendedAutoIds.length) {
    setSelectedUnitIds((prev) => {
      return [...new Set([...recommendedAutoIds, ...prev])];
    });
  }
}, [
  editingQueuedCallId,
  callType,
  location,
  nature,
  isCourtesy,
  recommendedAutoKey,
  setSelectedUnitIds,
]);

  useEffect(() => {
    if (!notesRef.current) return;
    notesRef.current.style.height = "26px";
    notesRef.current.style.height = `${Math.min(notesRef.current.scrollHeight, 54)}px`;
  }, [notes]);

  const formUnits = useMemo(() => {
    return availableUnits
      .filter((unit) => {
        if (isCourtesy) return isCourtesyAddableUnit(unit);
        return isResponseUnit(unit) || isSupervisor(unit) || isSupport(unit);
      })
      .sort(byPreferredUnitOrder);
  }, [availableUnits, isCourtesy]);

  const visibleUnitBoard = useMemo(() => [...units].sort(byPreferredUnitOrder), [units]);

  const addableUnits = useMemo(() => {
    return formUnits.filter((unit) => !selectedUnitIds.includes(unit.id));
  }, [formUnits, selectedUnitIds]);

  function toggleSelectedUnit(unitId) {
    setSelectedUnitIds((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );
  }

  function onSelectExtraUnit(unitId) {
    if (!unitId) return;
    if (!selectedUnitIds.includes(unitId)) {
      setSelectedUnitIds((prev) => [...prev, unitId]);
    }
  }

  if (mode === "units") {
    return (
      <div
        style={{
          minHeight: 0,
          height: "100%",
          display: "grid",
          gridTemplateRows: "auto minmax(0, 1fr)",
          gap: 6,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            ...panelStyle,
            overflow: "hidden",
            background: "linear-gradient(180deg, #13213a 0%, #0f172a 100%)",
            color: "#e2e8f0",
          }}
        >
          <div
            style={{
              padding: "6px 7px",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              display: "grid",
              gap: 5,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 6,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 8,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    opacity: 0.7,
                  }}
                >
                  Console
                </div>
                <div style={{ fontSize: 11, fontWeight: 900, marginTop: 1 }}>
                  {debug?.some((x) => String(x).includes("Not signed in")) ? "Offline" : "Connected"}
                </div>
              </div>

              <div
                style={{
                  fontSize: 8,
                  padding: "2px 6px",
                  borderRadius: 999,
                  fontWeight: 900,
                  border: "1px solid #334155",
                  background: "#172033",
                  color: "#cbd5e1",
                  whiteSpace: "nowrap",
                }}
              >
                {editingQueuedCallId ? "Editing" : "Ready"}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
              <SummaryBox label="Avail" value={unitSummary?.avail ?? 0} />
              <SummaryBox label="On Call" value={unitSummary?.onCall ?? 0} />
              <SummaryBox label="OOS" value={unitSummary?.oos ?? 0} />
            </div>
          </div>
        </div>

        <div
          style={{
            ...panelStyle,
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            background: "#0f172a",
            color: "#e2e8f0",
          }}
        >
          <div
            style={{
              padding: "6px 8px",
              borderBottom: "1px solid #22314d",
              fontSize: 9,
              fontWeight: 900,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              background: "#10192c",
              flex: "0 0 auto",
            }}
          >
            Units
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              padding: 6,
              display: "grid",
              gap: 4,
              alignContent: "start",
            }}
          >
            {visibleUnitBoard.length ? (
              visibleUnitBoard.map((unit) => {
                const onCall = !!unit.currentCallId;
                const style = getUnitChipStyle(unit.status);

                return (
                  <button
                    key={unit.id}
                    type="button"
                    onClick={() => onToggleUnitService(unit)}
                    disabled={onCall}
                    style={{
                      ...buttonBase,
                      width: "100%",
                      textAlign: "left",
                      justifyContent: "flex-start",
                      background: onCall ? "#1f2937" : style.background,
                      borderColor: onCall ? "#475569" : style.borderColor,
                      color: onCall ? "#94a3b8" : style.color,
                      opacity: onCall ? 0.9 : 1,
                      cursor: onCall ? "default" : "pointer",
                      minHeight: 24,
                      fontSize: 9,
                      padding: "4px 7px",
                      lineHeight: 1.05,
                    }}
                    title={onCall ? "Unit is currently assigned to a call" : "Toggle AVAIL / OOS"}
                  >
                    {unit.name} • {onCall ? "On Call" : unit.status}
                  </button>
                );
              })
            ) : (
              <div style={{ fontSize: 10, color: "#94a3b8" }}>No units loaded.</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...panelStyle,
        overflow: "hidden",
        background: "#0f172a",
        color: "#e2e8f0",
        minHeight: 0,
        height: "100%",
        display: "grid",
        gridTemplateRows: "auto minmax(0, 1fr)",
      }}
    >
      <div
        style={{
          padding: "6px 8px",
          borderBottom: "1px solid #22314d",
          background: "linear-gradient(180deg, #172554 0%, #0f172a 100%)",
          display: "grid",
          gap: 6,
        }}
      >
        <div
          style={{
            fontSize: 8,
            textTransform: "uppercase",
            letterSpacing: 0.7,
            opacity: 0.8,
            fontWeight: 900,
          }}
        >
          Call Entry
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: 4,
          }}
        >
          <button
            type="button"
            onClick={() => onCreateOrUpdateCall(!primaryActionIsQueue)}
            disabled={busy}
            style={{
              ...buttonBase,
              background: primaryActionIsQueue
                ? "#475569"
                : callType === "emergency"
                  ? "#b91c1c"
                  : "#15803d",
              color: "#fff",
              borderColor: primaryActionIsQueue
                ? "#94a3b8"
                : callType === "emergency"
                  ? "#ef4444"
                  : "#22c55e",
              minHeight: 28,
              fontSize: 10,
              padding: "4px 6px",
            }}
          >
            {primaryActionIsQueue ? "Queue" : "Send"}
          </button>

          <button
            type="button"
            onClick={() => onCreateOrUpdateCall(false)}
            disabled={busy || (primaryActionIsQueue && !editingQueuedCallId)}
            style={{
              ...buttonBase,
              background: "#1e293b",
              color: "#fff",
              borderColor: "#475569",
              minHeight: 28,
              fontSize: 10,
              padding: "4px 6px",
              opacity: primaryActionIsQueue && !editingQueuedCallId ? 0.5 : 1,
            }}
          >
            {editingQueuedCallId ? "Update" : "Queue"}
          </button>

          <button
            type="button"
            onClick={onReset}
            disabled={busy}
            style={{
              ...buttonBase,
              minHeight: 28,
              fontSize: 10,
              padding: "4px 10px",
            }}
          >
            Reset
          </button>
        </div>
      </div>

      <div
        style={{
          minHeight: 0,
          overflow: "hidden",
          padding: 6,
          display: "grid",
          gap: 6,
          alignContent: "start",
          gridAutoRows: "min-content",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
          {CALL_TYPES.map((type) => {
            const active = callType === type.value;
            return (
              <button
                key={type.value}
                type="button"
               onClick={() => {
  setCallType(type.value);

  if (type.value === "standby") {
    setNature("Standby");
  }

  if (type.value === "emergency" && nature === "Standby") {
    setNature("");
  }

  if (type.value !== "courtesyTransport") {
    setSelectedUnitIds([]);
  }
}} 
                style={{
                  ...buttonBase,
                  background: active
                    ? type.value === "emergency"
                      ? "#3b1013"
                      : "#3b2a0a"
                    : "#172033",
                  color: active ? "#f8fafc" : "#dbe4ee",
                  borderColor: active
                    ? type.value === "emergency"
                      ? "#b91c1c"
                      : "#d97706"
                    : "#334155",
                  minHeight: 28,
                  fontSize: 10,
                  padding: "4px 6px",
                }}
              >
                {type.label}
              </button>
            );
          })}
        </div>

        {!isCourtesy && (
          <TwoCol>
            <Field label="Location">
              <SearchableDropdown
                value={location}
                onChange={setLocation}
                options={config?.locations || []}
                placeholder="Search location..."
              />
            </Field>

            <Field label="Nature">
              <SearchableDropdown
                value={nature}
                onChange={setNature}
                options={config?.natures || []}
                placeholder="Search nature..."
              />
            </Field>
          </TwoCol>
        )}

        {isCourtesy && (
          <>
            <TwoCol>
              <Field label="Pick Up">
                <SearchableDropdown
                  value={pickupLocation}
                  onChange={setPickupLocation}
                  options={config?.locations || []}
                  placeholder="Search or type pick up..."
                  allowFreeText
                />
              </Field>

              <Field label="Drop Off">
                <SearchableDropdown
                  value={dropoffLocation}
                  onChange={setDropoffLocation}
                  options={config?.locations || []}
                  placeholder="Search or type drop off..."
                  allowFreeText
                />
              </Field>
            </TwoCol>

            <Field label="Passengers (optional)">
              <input
                type="number"
                min="1"
                value={passengerCount}
                onChange={(e) => setPassengerCount(e.target.value)}
                style={inputStyle}
                placeholder="Number of passengers"
              />
            </Field>
          </>
        )}

        <Field label="Notes">
          <textarea
            ref={notesRef}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={1}
            placeholder="Notes"
            style={{
              ...inputStyle,
              minHeight: 26,
              maxHeight: 54,
              padding: "5px 8px",
              resize: "none",
              overflowY: "auto",
              lineHeight: 1.1,
            }}
          />
        </Field>

        <MiniBox
          title="Recommended Units"
          sub={
            isCourtesy
              ? courtesyHasRecommendedUnit
                ? "Dedicated transport unit auto-selected."
                : "No transport unit is currently available. Queue the call or manually add another allowed unit."
              : "Click to toggle. Supervisor is suggested only until clicked."
          }
        >
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {!recommendedAutoIds.length && !recommendedSuggestedOnlyIds.length && (
              <span style={{ fontSize: 9, color: "#94a3b8" }}>None</span>
            )}

            {recommendedAutoIds.map((id) => {
              const unit = units.find((u) => u.id === id);
              const selected = selectedUnitIds.includes(id);

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggleSelectedUnit(id)}
                  style={{
                    ...chipButtonBase,
                    background: selected ? "#102844" : "#172033",
                    borderColor: selected ? "#2563eb" : "#334155",
                    color: selected ? "#bfdbfe" : "#cbd5e1",
                    minHeight: 22,
                    fontSize: 9,
                    padding: "2px 6px",
                    lineHeight: 1.05,
                  }}
                >
                  {selected ? "✓ " : ""}
                  {unit?.name || id}
                </button>
              );
            })}

            {recommendedSuggestedOnlyIds.map((id) => {
              const unit = units.find((u) => u.id === id);
              const selected = selectedUnitIds.includes(id);

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggleSelectedUnit(id)}
                  style={{
                    ...chipButtonBase,
                    background: selected ? "#23310b" : "#172033",
                    borderColor: selected ? "#65a30d" : "#475569",
                    color: selected ? "#d9f99d" : "#cbd5e1",
                    minHeight: 22,
                    fontSize: 9,
                    padding: "2px 6px",
                    lineHeight: 1.05,
                  }}
                >
                  {selected ? "✓ " : ""}
                  {unit?.name || id}
                </button>
              );
            })}
          </div>
        </MiniBox>

        <MiniBox
          title="Add Unit"
          sub={
            isCourtesy
              ? "Manual add-ons can include other allowed units."
              : undefined
          }
        >
          <select value="" onChange={(e) => onSelectExtraUnit(e.target.value)} style={inputStyle}>
            <option value="">Add extra unit…</option>
            {addableUnits.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </select>
        </MiniBox>

        {!!selectedUnitIds.length && (
          <MiniBox title="Sending With">
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {selectedUnitIds.map((id) => {
                const unit = units.find((u) => u.id === id);
                if (!unit) return null;

                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleSelectedUnit(id)}
                    style={{
                      ...chipButtonBase,
                      background: "#102844",
                      borderColor: "#2563eb",
                      color: "#bfdbfe",
                      minHeight: 22,
                      fontSize: 9,
                      padding: "2px 6px",
                      lineHeight: 1.05,
                    }}
                  >
                    {unit.name} ✕
                  </button>
                );
              })}
            </div>
          </MiniBox>
        )}
      </div>
    </div>
  );
}

function SummaryBox({ label, value }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 7,
        padding: 4,
      }}
    >
      <div style={{ fontSize: 7, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 900, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function TwoCol({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 8,
          fontWeight: 800,
          color: "#94a3b8",
          marginBottom: 3,
          textTransform: "uppercase",
          letterSpacing: 0.25,
          lineHeight: 1,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function MiniBox({ title, sub, children }) {
  return (
    <div
      style={{
        background: "#10192c",
        border: "1px solid #22314d",
        borderRadius: 8,
        padding: 6,
      }}
    >
      <div style={{ display: "grid", gap: 1, marginBottom: 4 }}>
        <div
          style={{
            fontSize: 8,
            fontWeight: 900,
            color: "#e2e8f0",
            textTransform: "uppercase",
            letterSpacing: 0.35,
            lineHeight: 1,
          }}
        >
          {title}
        </div>
        {!!sub && <div style={{ fontSize: 8, color: "#94a3b8", lineHeight: 1.1 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  borderRadius: 7,
  border: "1px solid #334155",
  background: "#0b1220",
  color: "#e2e8f0",
  minHeight: 28,
  padding: "0 8px",
  outline: "none",
  fontSize: 10,
  boxSizing: "border-box",
};